/**
 * Hợp đồng dữ liệu của phân hệ Bảo mật (hợp đồng triển khai ADR-0059, mục 6).
 *
 * Tên trường giữ **đúng như API** (`snake_case`). Kiểu của danh mục tham số ở
 * `nghiep-vu/types.ts` vì phép tính thuần cần chúng; tệp này gom lại cho phần
 * còn lại của giao diện. Khi `openapi/platform-api.yaml` có các endpoint này thì
 * sinh lại từ đó.
 */
export type {
  BanNhap,
  BienCaiDat,
  BienHieuLuc,
  BienTruong,
  DongCidr,
  HuongSiet,
  KenhThongBao,
  KieuCaiDat,
  MaGoi,
  MucCaiDat,
  MucDoThongBao,
  NguonGiaTri,
  PhamViCaiDat,
  SettingDefinition,
  TuyenThongBao,
  VaiTroSua,
} from './nghiep-vu/types';
export { CAC_GOI } from './nghiep-vu/types';
export type { TaiKhoanKhoaTam, TrangKhoaTam } from './nghiep-vu/khoa-tam';
export type {
  NguonAudit,
  NhomTheo,
  QuyetDinhAudit,
  QuyTacCanhBao,
  TruongQuyTac,
} from './nghiep-vu/quy-tac-canh-bao';
export type { BoLocCanhBao, CanhBaoAudit, TrangCanhBao, TrangThaiCanhBao } from './nghiep-vu/canh-bao-audit';

import type { MaGoi, MucCaiDat, MucDoThongBao, PhamViCaiDat, SettingDefinition } from './nghiep-vu/types';

export interface DanhMucCaiDat {
  settings: SettingDefinition[];
}

/** Trạng thái đồng bộ chính sách mật khẩu vào realm Keycloak (ADR-0059 mục 5.2). */
export type TrangThaiDongBo = 'SYNCED' | 'PENDING' | 'DISABLED';

/** `GET /api/v1/tenants/{id}/security`, `/security/platform`, `/security/plans/{code}`. */
export interface SecurityPolicyView {
  scope: PhamViCaiDat;
  scope_key: string;
  plan_code: MaGoi | null;
  /** Phiên bản hiện tại; cũng là `ETag`, gửi lại ở `If-Match`. */
  version: number;
  settings: MucCaiDat[];
  /** `null` khi tầng này chưa lưu lần nào. */
  updated_at: string | null;
  updated_by: string | null;
  /**
   * IP của người gọi như tenant-service thấy (sau `real-ip` của Gateway). Không
   * có thì màn hình bỏ các kiểm "tự khoá mình" phía giao diện; backend vẫn kiểm.
   */
  caller_ip?: string | null;
  /** Domain email đã xác minh của tenant, chỉ có ở tầng tenant (ADR-0099). */
  verified_domains?: string[];
  /** Chỉ có ở `GET/PUT /api/v1/security/password-policy`. */
  sync_status?: TrangThaiDongBo;
}

/** Thân `PUT` của mọi chính sách. `null` ở một khoá là về kế thừa. */
export interface GhiCaiDatVao {
  settings: Record<string, unknown>;
  reason?: string;
  mfa_apply?: 'NOW' | 'NEXT_LOGIN';
}

export interface ThayDoiPhienBan {
  key: string;
  before: unknown;
  after: unknown;
}

export interface PhienBanCaiDat {
  version: number;
  changed_by: string | null;
  changed_at: string;
  reason: string | null;
  changes: ThayDoiPhienBan[];
}

export interface TrangPhienBan {
  items: PhienBanCaiDat[];
  next_cursor: string | null;
}

/** `GET .../security/ip-monitor` — IP ngoài dải tin cậy khi tổ chức ở mức Chỉ ghi nhận (ADR-0090 mục 1). */
export interface BaoCaoIpMonitor {
  from: string;
  to: string;
  items: Array<{ ip: string; count: number; last_seen_at: string; user_ids: string[] }>;
}

/** Nguồn của một dòng blocklist: quản trị thêm, hoặc chặn tạm tự động khi đăng nhập sai dồn dập (ADR-0090 mục 3.2). */
export type NguonChan = 'MANUAL' | 'AUTO';

/** Một dòng blocklist, của nền tảng hay của một tenant (`IpBlocklistEntry`). */
export interface DongChanIp {
  id: string;
  cidr: string;
  reason: string;
  /** Blocklist nền tảng luôn `MANUAL`; bản API cũ chưa có trường này thì coi là `MANUAL`. */
  source?: NguonChan;
  expires_at: string | null;
  created_by: string | null;
  created_at: string;
}

/** Blocklist nào: của nền tảng (`/api/v1/security/ip-blocklist`) hay của một tenant (`/tenants/{id}/security/...`). */
export type PhamViChan = { loai: 'nen-tang' } | { loai: 'tenant'; tenantId: string };

/**
 * Thân `POST .../ip-blocklist`. Thời hạn gửi bằng số phút, server tự tính `expires_at` theo đồng hồ của nó
 * (ADR-0090 mục 3.1); `null` là vĩnh viễn.
 */
export interface ThemChanVao {
  cidr: string;
  reason: string;
  duration_minutes: number | null;
}

/** Kết quả `POST /api/v1/security/notifications/test`. */
export interface KetQuaGuiThu {
  status: 'SENT' | 'NO_RECIPIENT' | 'FAILED';
  recipients: string[];
}

/** Một dòng `notification_log`. */
export interface ThongBao {
  id: string;
  notify_key: string;
  tenant_id: string | null;
  severity: MucDoThongBao;
  title: string;
  body: string;
  recipients: string[];
  channels: string[];
  status: 'SENT' | 'NO_RECIPIENT' | 'FAILED';
  created_at: string;
}

export interface TrangThongBao {
  items: ThongBao[];
  next_cursor: string | null;
}

/**
 * Lỗi theo trường trong `errors` của `INVALID_ARGUMENT` (ProblemFactory của
 * platform-common): `pointer` có tiền tố `#` (`#/settings/network.ip_allowlist/2/cidr`),
 * `detail` là câu backend đã dịch sẵn — hiện nguyên văn, không dịch lại.
 */
export interface LoiTruong {
  pointer: string;
  detail: string;
}

/** Khoá tham số mà một `pointer` trỏ tới; không trỏ vào `/settings/...` thì `null`. */
export function khoaCuaPointer(pointer: string): string | null {
  const m = /^#?\/settings\/([^/]+)/.exec(pointer);
  return m ? m[1]!.replace(/~1/g, '/').replace(/~0/g, '~') : null;
}

/**
 * Mảng `errors` của một lỗi API: lớp vận chuyển (`lib/api/http.ts`) và dữ liệu
 * mẫu đều đặt nó ở `ApiError.details`. Lỗi tham số không có `pointer` thì về
 * chuỗi rỗng — `khoaCuaPointer` trả `null`, form hiện nó ở dải lỗi chung.
 */
export function loiTruongCua(loi: { details?: unknown } | null | undefined): LoiTruong[] {
  const ds = loi?.details;
  if (!Array.isArray(ds)) return [];
  return ds
    .filter((l): l is { pointer?: string; detail: string } => typeof l?.detail === 'string')
    .map((l) => ({ pointer: l.pointer ?? '', detail: l.detail }));
}
